import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin, Users, ChevronRight, Plus, Compass } from "lucide-react";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";

export default function MyTripsPage() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState({ hosted: [], joined: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/users/me/trips");
        setTrips(res.data);
      } catch (err) {
        console.error("Error fetching trips:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalTrips = (trips.hosted?.length || 0) + (trips.joined?.length || 0);

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">My Trips</h1>
            <p className="text-sm text-slate-500 mt-1">
              Plans you're hosting and crews you've joined.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3" data-testid="my-trips-skeleton">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 p-4 animate-pulse">
                <div className="h-4 w-2/3 bg-slate-100 rounded mb-3" />
                <div className="h-3 w-1/2 bg-slate-100 rounded" />
              </div>
            ))}
          </div>
        ) : totalTrips === 0 ? (
          <EmptyState onCreate={() => navigate("/create")} />
        ) : (
          <>
            {trips.hosted?.length > 0 && (
              <Section title="Hosting" tone="indigo" count={trips.hosted.length}>
                {trips.hosted.map((t) => (
                  <TripCard key={t.id} trip={t} role="host" onClick={() => navigate(`/trip/${t.id}`)} />
                ))}
              </Section>
            )}
            {trips.joined?.length > 0 && (
              <Section title="Joined" tone="emerald" count={trips.joined.length}>
                {trips.joined.map((t) => (
                  <TripCard key={t.id} trip={t} role="participant" onClick={() => navigate(`/trip/${t.id}`)} />
                ))}
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, tone, count, children }) {
  const tones = {
    indigo: "bg-indigo-100 text-indigo-700",
    emerald: "bg-emerald-100 text-emerald-700",
  };
  return (
    <div className="mb-7">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${tones[tone]}`}>{count}</span>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function TripCard({ trip, role, onClick }) {
  const start = trip.start_date ? new Date(trip.start_date) : null;
  const end = trip.end_date ? new Date(trip.end_date) : null;
  const dateLabel = start
    ? `${start.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}${
        end ? " – " + end.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : ""
      }`
    : "";

  return (
    <button
      onClick={onClick}
      data-testid={`trip-card-${trip.id}`}
      className="w-full bg-white text-left rounded-2xl border border-slate-100 hover:border-indigo-200 hover:shadow-md transition-all p-4 active:scale-[0.99]"
    >
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${role === "host" ? "bg-indigo-100" : "bg-emerald-100"}`}>
          <MapPin className={`w-5 h-5 ${role === "host" ? "text-indigo-700" : "text-emerald-700"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="font-semibold text-slate-900 truncate">{trip.trip_name}</h3>
            <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
          </div>
          <div className="text-sm text-slate-600 truncate">{trip.destination}</div>
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
            {dateLabel && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {dateLabel}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {trip.participants_count}/{trip.max_members}
            </span>
            {role === "host" && (
              <span className="text-indigo-600 font-semibold">You're hosting</span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="text-center py-12 px-4" data-testid="my-trips-empty">
      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-indigo-100 to-emerald-100 rounded-3xl flex items-center justify-center mb-5">
        <Compass className="w-10 h-10 text-indigo-600" />
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-2">No trips yet</h3>
      <p className="text-sm text-slate-600 mb-6 max-w-xs mx-auto">
        Plan one for your crew. Lock the date, share the link, and watch friends commit.
      </p>
      <button
        data-testid="empty-create-btn"
        onClick={onCreate}
        className="h-12 px-6 bg-indigo-600 text-white rounded-full font-semibold hover:bg-indigo-700 active:scale-95 transition-all inline-flex items-center gap-2 shadow-lg shadow-indigo-600/20"
      >
        <Plus className="w-4 h-4" />
        Plan your first trip
      </button>
    </div>
  );
}
